/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
//Grid for ruby horsethief for example.  See ui-permit/src/containers/DetailedAvailabilityConductor.jsx for more locations.

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import { Gnoment, gnoment } from 'shared-ui/gnoment';
import { Button, announce, Text } from 'sarsaparilla';
import cx from 'classnames';
import {
    isDayBeingBlockedByLottery,
    isDayManagedByLottery,
    isDayBlocked,
    isWalkupQuotaType,
    isDayWalkupOnly,
} from '../../utils/availability-utils-new';
import * as types from '../../constants';
import {
    readRuleValue,
    dateDiffs,
    getDaysBetween,
    getGroupSizeLabel,
} from '../../utils/utilsNew';
import StickyTableHeaderHelper from './StickyTableHeaderHelper';
import { Grid, Row, GridCell } from './Grid';
import AvailabilityTableHeader from './AvailabilityTableHeaderNew';
import AvailabilityCell from './AvailabilityCell';

export default class AvailabilityGrid extends React.Component {
    static propTypes = {
        quotaType: PropTypes.oneOf([types.QUOTA_TYPE_ADVANCED, types.QUOTA_TYPE_WALKUP]),
        divisionType: PropTypes.string,
        dataSource: PropTypes.arrayOf(
            PropTypes.shape({
                permit: PropTypes.shape({}),
            })
        ).isRequired,
        startDate: PropTypes.instanceOf(Gnoment).isRequired,
        endDate: PropTypes.instanceOf(Gnoment).isRequired,
        checkoutNextDay: PropTypes.bool,
        allowSameDayReservation: PropTypes.bool,
        itineraryStartNextDay: PropTypes.bool,
        allowFlexibleItinerary: PropTypes.bool,
        onSelectionChange: PropTypes.func.isRequired,
        stayLimit: PropTypes.number,
        stayLimitMetadata: PropTypes.shape({}),
        stayLimitRule: PropTypes.shape({}),
        rules: PropTypes.arrayOf(PropTypes.shape({})),
        loading: PropTypes.bool,
        selectedItinerary: PropTypes.arrayOf(PropTypes.shape({})),
        ignoreQuotaAfterFirstDay: PropTypes.bool,
        isBypassQuotaCheck: PropTypes.bool,
        hideDivisionLinks: PropTypes.bool,
        overlay: PropTypes.string,
        updateSort: PropTypes.func,
        sort: PropTypes.shape({
            column: PropTypes.string,
            isAsc: PropTypes.bool,
            isSorted: PropTypes.bool,
        }),
        onDivisionDetailsClick: PropTypes.func,
        numOfCols: PropTypes.number,
        isFullView: PropTypes.bool,
        isSmallerGrid: PropTypes.bool,
    };

    static defaultProps = {
        divisionType: 'Division',
        allowSameDayReservation: false,
        loading: false,
        stayLimit: 0,
        stayLimitMetadata: {},
        selectedItinerary: [],
        ignoreQuotaAfterFirstDay: false,
        itineraryStartNextDay: false,
        numOfCols: types.AVAILABILITY_DAYS_TO_DISPLAY,
    };

    state = {
        maskForSelectableDates: {},
        maskForSelectedDates: {},
        dataSource: [],
    };

    componentDidMount() {
        // note: use view_order attribute to sort, do not sort columns for grid yourself here
        this.refreshGrid(
            this.props.dataSource,
            this.props.selectedItinerary,
            this.props.sort
        );

        const { overlay } = this.props;
        if (overlay) {
            announce(`${overlay} to enable the detailed availability grid.`);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            (this.props.dataSource && prevProps.dataSource !== this.props.dataSource) ||
            (this.props.selectedItinerary &&
                prevProps.selectedItinerary !== this.props.selectedItinerary) ||
            (this.props.startDate && prevProps.startDate !== this.props.startDate) ||
            (this.props.endDate && prevProps.endDate !== this.props.endDate) ||
            prevProps.itineraryStartNextDay !== this.props.itineraryStartNextDay ||
            (this.props.sort && prevProps.sort !== this.props.sort)
        ) {
            this.refreshGrid(
                this.props.dataSource,
                this.props.selectedItinerary,
                this.props.sort
            );
        }

        if (prevProps.overlay !== this.props.overlay) {
            if (this.props.overlay) {
                announce(
                    `${this.props.overlay} to enable the detailed availability grid.`
                );
            } else {
                announce('The availability grid is enabled');
            }
        }
    }

    onColumnSortToggle(columnName, isSorted, isAsc) {
        this.props.updateSort({ column: columnName, isSorted, isAsc });
    }

    onNameSortToggle = (isSorted, isAsc) => {
        this.onColumnSortToggle('name', isSorted, isAsc);
    };

    getDayType = ({
        permit,
        currentDate,
        dateMap,
        quotas,
        quotaType,
        day,
        divisionId,
        isBypassQuotaCheck,
        aggSeasonRules,
    }) => {
        const availabilityProps = {
            permit,
            divisionId,
            currentDate,
            blockLotteryDates: true,
            quotaType,
            dateMap,
            quotas,
            aggSeasonRules,
            isBypassQuotaCheck,
        };
        const { availabilityCellStatuses } = types;

        if (isDayWalkupOnly(day, availabilityProps)) {
            return {
                className: 'walk-up',
                status: availabilityCellStatuses.WALK_UP,
                tooltips: ['Walkup', 'This is available at the facility on the day of'],
            };
        }

        if (isDayManagedByLottery(day, availabilityProps)) {
            if (isDayBeingBlockedByLottery(day, availabilityProps)) {
                if (isBypassQuotaCheck) {
                    return {
                        className: 'lottery',
                        status: availabilityCellStatuses.LOTTERY,
                        tooltips: ['This day is being managed by the lottery'],
                    };
                }
                return {
                    className: 'lottery',
                    status: availabilityCellStatuses.LOTTERY,
                    tooltips: [
                        'This day is being managed by the lottery',
                        'Please refer to important dates for application process',
                    ],
                };
            }
            return {
                className: 'lottery',
                status: availabilityCellStatuses.LOTTERY,
                tooltips: ['This lottery day is unavailable'],
            };
        }
        if (isDayBlocked(day, availabilityProps)) {
            return {
                className: 'unavailable',
                status: availabilityCellStatuses.UNAVAILABLE,
                tooltips: ['No online reservations available'],
            };
        }
        return {
            className: 'available',
            status: availabilityCellStatuses.AVAILABLE,
            tooltips: this.getTooltip({ dateMap, quotas, day }),
        };
    };

    getTooltip = ({ dateMap, quotas, day }) => {
        const tooltips = ['Available'];
        if (!dateMap) {
            return tooltips;
        }
        const date = day.startOf('day').format();
        for (const quota in quotas) {
            if (Object.hasOwn(quotas, quota)) {
                let remaining = get(quotas, `[${quota}][${date}].remaining`, 0);
                let total = get(quotas, `[${quota}][${date}].total`, 0);

                if (remaining > 99) {
                    remaining = '99+';
                } else if (remaining < 0) {
                    remaining = 0;
                }
                if (total > 99) {
                    total = '99+';
                } else if (total < 0) {
                    total = 0;
                }

                const remainingTotalString = `${remaining} out of ${total}`;
                if (
                    quota === types.GROUP_AVAILABILITY_KEY_NAME ||
                    quota === types.GROUP_AVAILABILITY_DAILY_KEY_NAME
                ) {
                    tooltips.push(`Groups: ${remainingTotalString}`);
                }
                if (
                    quota === types.MEMBER_AVAILABILITY_KEY_NAME ||
                    quota === types.MEMBER_AVAILABILITY_DAILY_KEY_NAME
                ) {
                    const peopleLabel = getGroupSizeLabel(
                        get(this.props, 'dataSource[0].permit')
                    );
                    tooltips.push(`${peopleLabel}: ${remainingTotalString}`);
                }
                if (quota === types.WATERCRAFT_AVAILABILITY_KEY_NAME) {
                    tooltips.push(`Watercraft: ${remainingTotalString}`);
                }
                if (quota === types.MAX_STOCK_KEY_NAME) {
                    tooltips.push(`Stock: ${remainingTotalString}`);
                }
            }
        }
        return tooltips;
    };

    // set all dates between startDate and endDate within the provided dateMap to the given value (default true)
    unmask = (dateMap, startDate, endDate, inclusivityFlags, desiredValue) => {
        let valueToSet = desiredValue;
        if (valueToSet === undefined) {
            valueToSet = true;
        }

        const copy = { ...dateMap };
        const keys = getDaysBetween(dateMap, startDate, endDate, inclusivityFlags);

        keys.forEach((date) => {
            copy[date] = valueToSet;
        });

        return copy;
    };

    handleClick = (permitId, divisionId, date) => {
        let previousEvent;
        if (this.props.selectedItinerary.length !== 0) {
            previousEvent =
                this.props.selectedItinerary[this.props.selectedItinerary.length - 1];
        }

        // if the last event is a single day event
        // and the user is clicking on it, delete the event entirely
        if (
            previousEvent &&
            previousEvent.permit.id === permitId &&
            previousEvent.division.id === divisionId &&
            previousEvent.startDate === date &&
            previousEvent.endDate === date
        ) {
            this.updateItineraryEvents(this.props.selectedItinerary.slice(0, -1));
            return;
        }

        // if the last event is a multi day event
        // and the user is clicking on the endDate of the event
        // transform the multi day event into a single day event
        if (
            previousEvent &&
            previousEvent.permit.id === permitId &&
            previousEvent.division.id === divisionId &&
            previousEvent.endDate === date
        ) {
            previousEvent.endDate = previousEvent.startDate;
            this.updateItineraryEvents(this.props.selectedItinerary);
            return;
        }

        const { permit } = this.props.dataSource[0];
        const isWalkup = isWalkupQuotaType(this.props.quotaType);
        const isEntryDateOnly =
            readRuleValue(types.GROUP_AVAILABILITY_KEY_NAME, permit.rules) ||
            readRuleValue(types.MEMBER_AVAILABILITY_KEY_NAME, permit.rules);
        const isExtendingEntryDateOnly = previousEvent && isEntryDateOnly;
        const isExtendingEntryDateOnlyWalkup = isExtendingEntryDateOnly && isWalkup;

        // if the user is clicking on an unselectable date (walkups in the past are OK), ignore the click
        if (
            !this.state.maskForSelectableDates[divisionId][date] ||
            isExtendingEntryDateOnlyWalkup
        ) {
            // in field sales, let the user click an "unselectable date" if the permit is a isEntryDateOnly
            // write over the original event and update it.
            if (isExtendingEntryDateOnly) {
                previousEvent.division = this.props.dataSource.filter(
                    (data) => data.division.id === divisionId
                )[0].division;
                previousEvent.startDate = date;
                previousEvent.endDate = date;
                this.updateItineraryEvents(this.props.selectedItinerary);
                return;
            }
            if (!isWalkup) {
                if (!this.props.isBypassQuotaCheck) return;
            }
        }

        // if the last event is in the same division as the new event
        // it means the user is extending the last event
        if (
            previousEvent &&
            previousEvent.permit.id === permitId &&
            previousEvent.division.id === divisionId
        ) {
            previousEvent.endDate = date;
            this.updateItineraryEvents(this.props.selectedItinerary);
            return;
        }

        // Otherwise, we are creating a brand new single day event
        const newEvent = {
            permit: this.props.dataSource[0].permit,
            division: this.props.dataSource.filter(
                (data) => data.division.id === divisionId
            )[0].division,
            startDate: date,
            endDate: date,
        };

        // Some permits have checkout pre-selected for the next day!
        if (this.props.checkoutNextDay) {
            newEvent.endDate = gnoment(date)
                .add(1, 'd')
                .format(types.AVAILABILITY_KEY_MAP_FORMAT);
        }

        // add new event to previous events and save
        this.updateItineraryEvents(
            this.props.selectedItinerary.slice().concat(newEvent),
            true
        );
    };

    updateItineraryEvents(newItineraryEvents, shouldUpdateDivisionStayLimit) {
        this.props.onSelectionChange(newItineraryEvents, shouldUpdateDivisionStayLimit);
    }

    // update mask directly
    maskBlockedDays(mask, dataObject) {
        const newMask = { ...mask };
        dataObject.forEach((data) => {
            Object.keys(data.dateMap).forEach((date) => {
                newMask[data.division.id][date] =
                    newMask[data.division.id][date] && data.dateMap[date];
            });
        });
        return newMask;
    }

    rebuildMaskForSelectedDates = (dataObject = [], itineraryEvents) => {
        const mask = {};

        if (dataObject.length === 0) return mask;

        // initially set everything as masked
        dataObject.forEach((data) => {
            mask[data.division.id] = { ...data.dateMap };
            Object.keys(mask[data.division.id]).forEach(
                (key) => (mask[data.division.id][key] = false)
            );
        });

        // unmask selected items
        itineraryEvents.forEach((itineraryEvent) => {
            // single day
            if (itineraryEvent.startDate === itineraryEvent.endDate) {
                mask[itineraryEvent.division.id][itineraryEvent.startDate] =
                    types.AVAILABILITY_SINGLE_DAY;
                return;
            }

            // multi day
            mask[itineraryEvent.division.id][itineraryEvent.startDate] =
                types.AVAILABILITY_START_MULTIDAY;
            mask[itineraryEvent.division.id][itineraryEvent.endDate] =
                types.AVAILABILITY_END_MULTIDAY;

            const divisionMask = { ...mask[itineraryEvent.division.id] };

            // we need to augment the generated division mask to allow reservations outside of availability
            const endMoment = gnoment(itineraryEvent.endDate);
            let currentDay = gnoment(itineraryEvent.startDate).add(1, 'day');
            if (currentDay.isBefore(endMoment)) {
                while (!currentDay.isSame(endMoment, 'day')) {
                    divisionMask[currentDay.format(types.AVAILABILITY_KEY_MAP_FORMAT)] =
                        types.AVAILABILITY_CONTINUE_MULTIDAY;
                    currentDay = currentDay.add(1, 'day');
                }
            }

            mask[itineraryEvent.division.id] = this.unmask(
                divisionMask,
                gnoment(itineraryEvent.startDate).utc(),
                gnoment(itineraryEvent.endDate).utc(),
                '()',
                types.AVAILABILITY_CONTINUE_MULTIDAY
            );
        });
        return mask;
    };

    determineWeekendStayLimitFlags(lastEvent) {
        let previousDivisionId = '';
        if (this.props.selectedItinerary && this.props.selectedItinerary.length) {
            const previousEvent =
                this.props.selectedItinerary[this.props.selectedItinerary.length - 1];
            previousDivisionId = previousEvent ? previousEvent.division.id : '';
        }
        // last division selected in group with divisions that apply to stay limit rule and on last day (or no days remaining)
        if (
            types.WEEKEND_STAY_LIMIT_IDS.includes(lastEvent.division.id) &&
            this.props.stayLimit <= 1
        ) {
            return {
                weekendStayLimitFlag: true,
                unclickedWeekendStayLimitDivision: false,
            };
        }
        if (
            previousDivisionId &&
            !types.WEEKEND_STAY_LIMIT_IDS.includes(lastEvent.division.id) &&
            types.WEEKEND_STAY_LIMIT_IDS.includes(previousDivisionId)
        ) {
            // if current stay limit is zero because you are in a blackrock
            // make sure the blackrock children are blocked too
            return {
                weekendStayLimitFlag: true,
                unclickedWeekendStayLimitDivision: true,
            };
        }
        return {
            weekendStayLimitFlag: false,
            unclickedWeekendStayLimitDivision: false,
        };
    }

    // build a dateMap for every division.  If the dateMap for a date is 'true', that date is selectable
    rebuildMaskForSelectableDates = (dataObject = [], itineraryEvents) => {
        let mask = {};

        if (dataObject.length === 0) return mask;

        // initially set everything as masked
        dataObject.forEach((data) => {
            mask[data.division.id] = { ...data.dateMap };
            Object.keys(mask[data.division.id]).forEach(
                (key) => (mask[data.division.id][key] = false)
            );
        });
        // if there are no events, unmask all divisions that have isStartingPoint = true
        if (itineraryEvents.length === 0) {
            dataObject.forEach((data) => {
                if (data.division.isStartingPoint) {
                    // If the day is before the secret visibility for rolling quota, set the availability to true
                    Object.keys(mask[data.division.id]).forEach((key) => {
                        if (!data?.secretMap?.[key]) {
                            mask[data.division.id][key] = true;
                        }
                    });
                }
            });
            // and mask any blocked dates
            mask = this.maskBlockedDays(mask, dataObject);
        }
        // otherwise ...
        else {
            // compute how many days were used for each segment
            const daysUsedPerDivision = itineraryEvents.reduce(
                (runningTotalPerDivision, event) => {
                    const copy = { ...runningTotalPerDivision };
                    if (!runningTotalPerDivision[event.division.id]) {
                        copy[event.division.id] = 0;
                    }
                    copy[event.division.id] += dateDiffs(
                        gnoment(event.startDate),
                        gnoment(event.endDate),
                        false
                    );
                    return copy;
                },
                {}
            );

            // compute how many days were used overall
            let totalDaysUsed = itineraryEvents.reduce((total, event) => {
                return (
                    total +
                    dateDiffs(gnoment(event.startDate), gnoment(event.endDate), true)
                );
            }, 0);
            totalDaysUsed += 1; // count end

            const firstEvent = itineraryEvents[0];
            const lastEvent = itineraryEvents[itineraryEvents.length - 1];
            totalDaysUsed = dateDiffs(
                gnoment(firstEvent.startDate),
                gnoment(lastEvent.endDate)
            );

            // compute the maximum distance the last selection could be extended by
            const lastEventDivision = dataObject.filter(
                (data) => data.division.id === lastEvent.division.id
            )[0].division;
            let seasonalStayLimit = this.props.stayLimit;
            if (this.props.isBypassQuotaCheck) {
                seasonalStayLimit = types.OVERRIDE_STAY_LIMIT_MAX; // override season stay to use max value of 30 days
            }

            let totalRemainingDays = 0;
            //check if MaxStayOneWeekendNight (ex: blackrock) and if total days used is 5 (number of weekdays)
            if (
                this.props.divStayLimit?.operation === 'MaxStayOneWeekendNight' &&
                totalDaysUsed < 5
            ) {
                totalRemainingDays = seasonalStayLimit;
            } else {
                totalRemainingDays = Math.max(seasonalStayLimit - totalDaysUsed, 0);
            }

            const divisionRemainingDays =
                lastEventDivision.stayLimit - daysUsedPerDivision[lastEvent.division.id];

            let remainingDays;
            if (this.props.divStayLimit?.operation === 'MaxStayOneWeekendNight') {
                remainingDays = divisionRemainingDays;
            } else {
                remainingDays = Math.min(totalRemainingDays, divisionRemainingDays);
            }

            const { weekendStayLimitFlag, unclickedWeekendStayLimitDivision } =
                this.determineWeekendStayLimitFlags(lastEvent);

            // if we're ignoring quota after the first day, unblock the maximum distance
            if (
                lastEvent.endDate instanceof Date &&
                !isNaN(lastEvent.endDate) &&
                this.props.ignoreQuotaAfterFirstDay
            ) {
                mask[lastEvent.division.id] = this.unmask(
                    mask[lastEvent.division.id],
                    gnoment(lastEvent.endDate).utc(),
                    gnoment(lastEvent.endDate).utc().clone().add(remainingDays, 'days'),
                    '(]'
                );
                // otherwise...
            } else if (totalRemainingDays > 0) {
                // limit that maximum distance by blocking the selection upon encountering the first instance of a blocked date
                const allPossibleDays = getDaysBetween(
                    mask[lastEvent.division.id],
                    gnoment(lastEvent.endDate).utc(),
                    gnoment(lastEvent.endDate).utc().clone().add(remainingDays, 'days'),
                    '(]'
                );
                const lastEventData = dataObject.filter(
                    (data) => data.division.id === lastEvent.division.id
                )[0];
                let foundBlocker = false;

                let remainingDaysForCurrent = remainingDays;
                allPossibleDays.forEach((date, index) => {
                    if (foundBlocker) {
                        return;
                    }
                    if (!lastEventData.dateMap[date]) {
                        remainingDaysForCurrent = index;
                        foundBlocker = true;
                    }
                });
                // unmask the days that can be extended
                mask[lastEvent.division.id] = this.unmask(
                    mask[lastEvent.division.id],
                    gnoment(lastEvent.endDate).utc(),
                    gnoment(lastEvent.endDate)
                        .utc()
                        .clone()
                        .add(remainingDaysForCurrent, 'days'),
                    '(]'
                );

                // enable children (if selected)
                lastEvent.division.children.forEach((childId) => {
                    const canChildBeUnmaskedWithoutSameDayReservation =
                        totalRemainingDays > 0 &&
                        lastEvent.endDate !== lastEvent.startDate;
                    const canChildBeUnmaskedWithSameDayReservation = totalDaysUsed >= 0;
                    const stayLimitMetadata = get(
                        this.props,
                        `stayLimitMetadata.${childId}`,
                        ''
                    );
                    /*
            Children come in three different flavors:
              - default
              - staylimit weekend (ex: blackrock) and staylimit weekend on unclick (this isn't present lastEvent)
              - staylimit weekend downstream
          */

                    if (
                        (this.props.itineraryStartNextDay && !weekendStayLimitFlag) || // non-staylimit available days (default case)
                        (this.props.itineraryStartNextDay &&
                            weekendStayLimitFlag &&
                            !types.WEEKEND_STAY_LIMIT_IDS.includes(childId)) || // staylimit weekend children should be skipped
                        (this.props.itineraryStartNextDay &&
                            weekendStayLimitFlag &&
                            unclickedWeekendStayLimitDivision) || // unclicked and previous selection had weekend staylimit
                        (this.props.itineraryStartNextDay &&
                            !weekendStayLimitFlag &&
                            stayLimitMetadata) // actual downstream children beyond staylimit weekend
                    ) {
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                            '[]'
                        );
                    } else if (this.props.allowFlexibleItinerary) {
                        // unmask childId for today and tomorrow!
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc(),
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd'),
                            '[]'
                        );
                    } else if (
                        this.props.allowSameDayReservation
                            ? canChildBeUnmaskedWithSameDayReservation
                            : canChildBeUnmaskedWithoutSameDayReservation
                    ) {
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc(),
                            gnoment(lastEvent.endDate).utc(),
                            '[]'
                        );
                    }
                });
                // and finally mask any blocked dates
                this.maskBlockedDays(mask, dataObject);
            } else if (totalRemainingDays === 0) {
                // last day, can still unmask children to complete a trip (pere marquette uses this)
                lastEvent.division.children.forEach((childId) => {
                    const canChildBeUnmaskedWithoutSameDayReservation =
                        totalRemainingDays > 0 &&
                        lastEvent.endDate !== lastEvent.startDate;
                    const canChildBeUnmaskedWithSameDayReservation = totalDaysUsed >= 0;
                    const stayLimitMetadata = get(
                        this.props,
                        `stayLimitMetadata.${childId}`,
                        ''
                    );
                    if (
                        this.props.allowFlexibleItinerary ||
                        this.props.allowSameDayReservation
                            ? canChildBeUnmaskedWithSameDayReservation
                            : canChildBeUnmaskedWithoutSameDayReservation
                    ) {
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc(),
                            gnoment(lastEvent.endDate).utc(),
                            '[]'
                        );
                    } else if (
                        (weekendStayLimitFlag && stayLimitMetadata) ||
                        (!weekendStayLimitFlag &&
                            !types.WEEKEND_STAY_LIMIT_IDS.includes(childId)) ||
                        unclickedWeekendStayLimitDivision
                    ) {
                        // if metadata exists, apply "1" to it right now so that we have downstream openings
                        mask[childId] = this.unmask(
                            mask[childId],
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd').utc(),
                            gnoment(lastEvent.endDate).utc().clone().add(1, 'd').utc(),
                            '[]'
                        );
                    }
                });
                // and finally mask any blocked dates
                this.maskBlockedDays(mask, dataObject);
            }
        }
        return mask;
    };

    refreshGrid = (dataObject, selectedItinerary, sort) => {
        const maskForSelectableDates = this.rebuildMaskForSelectableDates(
            dataObject,
            selectedItinerary
        );

        // sort the divisions based on the current sort
        const dataSource = orderBy(
            dataObject,
            `division.${sort.column}`,
            sort.isAsc ? 'asc' : 'desc'
        );

        this.setState({
            dataSource,
            maskForSelectableDates,
            maskForSelectedDates: this.rebuildMaskForSelectedDates(
                dataObject,
                selectedItinerary
            ),
        });
    };

    renderAvailabilityCell(divisionData, index) {
        const { maskForSelectableDates: selectable, maskForSelectedDates: selected } =
            this.state;

        const { quotaType, isBypassQuotaCheck } = this.props;

        const { displayMap, dateMap, division, permit, quotas, aggSeasonRules } =
            divisionData;

        const permitDate = gnoment(this.props.startDate);
        const day = permitDate.clone().add(index, 'day');
        const cellDate = day.utc().startOf('day');
        const dateKey = cellDate.format(types.AVAILABILITY_KEY_MAP_FORMAT);
        const isSelectedCell = selected[division.id][dateKey];
        const dayType = this.getDayType({
            permit,
            currentDate: gnoment().utc(),
            dateMap,
            quotas,
            quotaType,
            day,
            divisionId: division.id,
            isBypassQuotaCheck,
            aggSeasonRules,
        });
        const avail = selectable[division.id][dateKey];

        let content = `${(displayMap && displayMap[dateKey]) || 0}`;

        const num = displayMap[dateKey];
        if (num > 99) {
            content = '99+';
        } else if (num > 0 && avail) {
            content = `${num}`;
        } else if (num < 0) {
            content = '0';
        } else if (num > 0 && !avail) {
            content = '0';
        }

        if (isBypassQuotaCheck) {
            if (dayType.className === 'lottery') {
                content = `L-${content}`;
            }
        } else if (!avail) {
            // if lottery or walkup, overwrite content here
            if (dayType.className === 'lottery') {
                content = 'L';
            } else if (dayType.className === 'walk-up') {
                content = 'W';
            } else {
                dayType.className = 'unavailable';
            }
            if (content === '0') {
                dayType.className = 'unavailable';
            }
        } else if (dayType.className === 'unavailable' && avail) {
            dayType.className = 'available';
            dayType.tooltips = this.getTooltip({ dateMap, quotas, day });
        }

        return (
            <AvailabilityCell
                key={index}
                content={content}
                className={dayType.className}
                tooltips={dayType.tooltips}
                isBypassQuotaCheck={!!isBypassQuotaCheck}
                isSelected={isSelectedCell}
                onSelectToggle={() => this.handleClick(permit.id, division.id, dateKey)}
                screenReaderText={`${division.name} on ${cellDate.format('MMMM D, YYYY')}`}
                status={dayType.status}
            />
        );
    }

    renderRow(divisionData, hasAreaData) {
        const { hideDivisionLinks, onDivisionDetailsClick, numOfCols, isSmallerGrid } =
            this.props;
        const { division } = divisionData;
        const { name, id: divisionId, district } = division;

        let divisionName = <Text>{name}</Text>;
        if (onDivisionDetailsClick && !hideDivisionLinks) {
            divisionName = (
                <Text>
                    <Button
                        appearance="link"
                        onClick={() => onDivisionDetailsClick(divisionId)}
                    >
                        {name}
                    </Button>
                </Text>
            );
        }

        let divisionComponent = divisionName;
        if (isSmallerGrid && hasAreaData) {
            divisionComponent = (
                <>
                    {divisionName}
                    <Text>{district}</Text>
                </>
            );
        }

        return (
            <Row key={divisionId}>
                <GridCell>
                    <div translate="no">{divisionComponent}</div>
                </GridCell>
                {hasAreaData && !isSmallerGrid && (
                    <GridCell>
                        <div translate="no">{district}</div>
                    </GridCell>
                )}
                {range(numOfCols).map((i) =>
                    this.renderAvailabilityCell(divisionData, i)
                )}
            </Row>
        );
    }

    render() {
        const { loading, isSmallerGrid, numOfCols } = this.props;

        if (loading) {
            return <div>Loading</div>;
        }

        const hasAreaData = !!this.state.dataSource?.[0]?.division?.district;

        const { overlay } = this.props;
        const titleToUse = overlay
            ? `${overlay} to view this table.`
            : `Availability by ${this.props.divisionType} and Dates`;

        return (
            <div className="per-availability-table-container">
                {!overlay && (
                    <StickyTableHeaderHelper
                        scrollingContainerId="per-availability-main"
                        isFullView={this.props.isFullView}
                    >
                        <div
                            aria-hidden="true"
                            className="sticky-availability-table-header"
                        >
                            <Grid
                                className={cx('detailed-availability-grid-new', {
                                    'has-area-data': hasAreaData && !isSmallerGrid,
                                    'mobile-app': isSmallerGrid,
                                })}
                            >
                                <AvailabilityTableHeader
                                    divisionType={this.props.divisionType}
                                    hasAreaData={hasAreaData && !isSmallerGrid}
                                    sort={this.props.sort}
                                    startDate={this.props.startDate}
                                    onNameSortToggle={this.onNameSortToggle}
                                    onAreaSortToggle={this.onAreaSortToggle}
                                    numOfCols={numOfCols}
                                />
                            </Grid>
                        </div>
                    </StickyTableHeaderHelper>
                )}

                <Grid
                    className={cx('detailed-availability-grid-new', {
                        'has-area-data': hasAreaData && !isSmallerGrid,
                        'mobile-app': isSmallerGrid,
                    })}
                    caption={`Availability by ${this.props.divisionType} and Dates`}
                >
                    <AvailabilityTableHeader
                        divisionType={this.props.divisionType}
                        hasAreaData={hasAreaData && !isSmallerGrid}
                        sort={this.props.sort}
                        startDate={this.props.startDate}
                        onNameSortToggle={this.onNameSortToggle}
                        onAreaSortToggle={this.onAreaSortToggle}
                        numOfCols={numOfCols}
                    />
                    <div className="per-availability-table" aria-label={titleToUse}>
                        {this.state.dataSource.map((divisionData) => {
                            return this.renderRow(divisionData, hasAreaData);
                        })}
                    </div>
                </Grid>
            </div>
        );
    }
}
